import React, { useEffect } from 'react';
import { Button } from 'reactstrap';

import PropControl, { DSsetValue } from "../base/components/PropControl";
import { encURI } from '../base/utils/miscutils';
import CharityControls from './CharityControls';
import C from '../C';
import KStatus from '../base/data/KStatus';
import Advert from '../base/data/Advert';
import List from '../base/data/List';
import DataStore from '../base/plumbing/DataStore';
import { getDataItem } from '../base/plumbing/Crud';
import Money from '../base/data/Money';
import { A } from '../base/plumbing/glrouter';


const isStartComplete = advert => (advert.format && advert.name && advert.campaign);

/** Advert format specs for the selector buttons */
const formats = [
	{ fmt: 'video', name: 'Watch to Donate', img: '/img/temp/brand-awareness.jpg' },
	// { fmt: 'banner', name: 'Banner', img: '/img/temp/web-traffic.png' },
	{ fmt: 'social', name: 'Social Media Ad', img: '/img/temp/social-buzz.png' },
];

/**
 * A row of buttons for picking advert format (currently just video/social)
 * @param {Object} p
 * @param {Advert} p.advert The Advert object to edit
 * @param {String[]} p.path DataStore path for the advert
 * @returns {React.Element}
*/
function FormatPicker({advert, path}) {
	const { format } = advert;
	const setFormat = fmt => DSsetValue(path.concat('format'), fmt);

	return <>
		<label className="large">What format of advert is this?</label>
		<div>
			{formats.map(({fmt, name, img}) => (
				<Button size="lg" color={format === fmt ? 'primary' : 'secondary'} onClick={() => setFormat(fmt)} className="mr-1">
					<h4>{name}</h4>
					<img className="logo-large" src={img} alt={fmt} />
				</Button>
			))}
		</div>
	</>;
}

/**
 * A PCDI for picking an advert's advertiser - with a modelValueFromInput function
 * to also pull out its name to update the advert's cached vertiserName field.
 * @param {String[]} path The path to the advert
 * @returns {React.Element}
 */
function SelectAdvertiser({ path }) {
	const mvfi = id => {
		const pvAdvertiser = getDataItem({ id, type: C.TYPES.Advertiser, status: KStatus.PUBLISHED });
		pvAdvertiser.promise.then(advertiser => {
			DataStore.setValue(path.concat('vertiserName'), advertiser.name);
		});
		return id;
	};

	return <PropControl prop="vertiser" type="DataItem" label="Advertiser" itemType="Advertiser" size="lg"
		modelValueFromInput={mvfi} path={path}
	/>;
}


function GoalControls ({advert, path}) {
	Advert.assIsa(advert);
	// pick a name
	if (advert.campaign && !advert.name) {
		// ??how can we nicely wait for the user to stop typing the campaign name?
		const campaignId = advert.campaign;
		let pvCampaign = getDataItem({type: C.TYPES.Campaign, id: campaignId, status: KStatus.PUB_OR_DRAFT, swallow: true });
		let pvAds = pvCampaign.value && Advert.fetchForCampaign({campaignId, status: KStatus.DRAFT});
		if (pvAds && pvAds.value) {
			const otherAdsCount = List.hits(pvAds.value).filter(a => a.id !== advert.id).length;
			advert.name = `${campaignId} Ad ${otherAdsCount + 1}`;
		}
	}

	// HACK: Budget deprecated & controls removed, but we still need to be able to set total donation pledge for display purposes
	// For ads with existing budget, autofill totalPledged from topLineItem total * 0.5 - as the adserver does
	useEffect(() => {
		if (advert.totalPledged) return;
		const total = advert.topLineItem?.budget?.total;
		if (!total || !Money.value(total)) return;
		// defer to stop update-during-render warning
		window.setTimeout(() => DataStore.setValue([...path, 'totalPledged'], Money.mul(total, 0.5)));
	}, []);

	return <div>
		<PropControl size="lg" label="Your website landing page" type="url" item={advert} path={path} prop="url" https
			help="Where would you like the user to click through to?" required
		/>
		<FormatPicker advert={advert} path={path} />

		<PropControl className="mt-3" size="lg" label="Select or create your campaign" item={advert} path={path} prop="campaign"
			canCreate
			type="DataItem" itemType={C.TYPES.Campaign} status={KStatus.PUB_OR_DRAFT}
			help="This must match the SalesForce Job Number / Xero Campaign Number. Use a shared campaign to group activity across a set of related adverts."
		/>

		<PropControl size="lg" label="A name for this specific Advert" item={advert} path={path} prop="name" />
		<PropControl type="Money" size="lg" label="Total pledge to charity" item={advert} path={path} prop="totalPledged" />

		<h3>What good causes will this Advert support? Pick up to three.</h3>
		<CharityControls entity={advert} />

		{advert.parent && <div><small><A href={`/#advert/${encURI(advert.parent)}`}>parent advert</A></small></div>}

		<div className="basic-info">
			{advert.branding?.logo ? (
				<img src={advert.branding.logo} alt="brand logo" className="img-thumbnail logo-large" />
			) : null}
			<SelectAdvertiser path={path} />
		</div>
		<div className="extra-settings">
			<PropControl label="Hide this ad from being showcased?" path={path} prop="hideFromShowcase" type="checkbox" help="Ads are showcased in places like the Impact dashboard. Check this to prevent this ad from showing up in a showcase."/>
		</div>

		{/* <PropControl prop="tags" path={path} label="Tags" type="pills" help="Miscellaneous tags. Can be used for filtering in the Impact Hub."/> */}
	</div>;
}


export default GoalControls;
export {
	isStartComplete
};
